import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Check, Mail, RefreshCw, AlertTriangle } from 'lucide-react';
import api from '../../api/axios';
import DataGrid from '../../components/DataGrid';

/* ── Types ───────────────────────────────────────────────────────────────── */

type Plan         = 'FREE' | 'STARTER' | 'PRO';
type InviteStatus = 'PENDING' | 'USED' | 'EXPIRED' | 'REVOKED';

interface Invite {
  id: string;
  societyName: string;
  contactEmail?: string;
  email?: string;
  invitedEmail?: string;
  plan: Plan;
  inviteUrl: string;
  createdAt: string;
  expiresAt: string;
  status: InviteStatus;
}

interface AccessRequest {
  id: string;
  contactName: string;
  email: string;
  societyName: string;
  city?: string;
  phone?: string;
  createdAt: string;
  status: string;
}

interface Paged<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
}

type Envelope<T> = { success?: boolean; data?: T } | T;
function unwrap<T>(raw: Envelope<T>): T {
  return (raw as { data?: T })?.data ?? (raw as T);
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const fmt = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const STATUS_STYLE: Record<InviteStatus, { color: string; bg: string; label: string }> = {
  PENDING: { color: '#D97706', bg: 'rgba(245,158,11,0.1)',    label: 'Pending' },
  USED:    { color: '#059669', bg: 'rgba(16,185,129,0.1)',    label: 'Used'    },
  EXPIRED: { color: '#94A3B8', bg: 'rgba(148,163,184,0.12)', label: 'Expired' },
  REVOKED: { color: '#DC2626', bg: 'rgba(239,68,68,0.1)',     label: 'Revoked' },
};

const PLAN_STYLE: Record<Plan, { color: string; bg: string }> = {
  FREE:    { color: '#64748B', bg: 'rgba(100,116,139,0.1)' },
  STARTER: { color: '#1A6CF6', bg: 'rgba(26,108,246,0.1)'  },
  PRO:     { color: '#7C3AED', bg: 'rgba(109,40,217,0.1)'  },
};

const Badge: React.FC<{ label: string; color: string; bg: string }> = ({ label, color, bg }) => (
  <span style={{ padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, color, background: bg }}>
    {label}
  </span>
);

/* ── InviteManagementPage ────────────────────────────────────────────────── */

const PAGE_SIZE = 10;
const PLAN_OPTS: Plan[] = ['FREE', 'STARTER', 'PRO'];

const InviteManagementPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<'invites' | 'requests'>('invites');

  /* form */
  const [societyName,  setSocietyName]  = useState(searchParams.get('society') ?? '');
  const [contactName,  setContactName]  = useState(searchParams.get('name')    ?? '');
  const [email,        setEmail]        = useState(searchParams.get('email')   ?? '');
  const [planField,    setPlanField]    = useState<Plan>('STARTER');
  const [expiry,      setExpiry]      = useState(72);
  const [generating,  setGenerating]  = useState(false);
  const [genError,    setGenError]    = useState('');
  const [inviteUrl,   setInviteUrl]   = useState('');
  const [copied,      setCopied]      = useState(false);
  const [sending,     setSending]     = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError,   setSendError]   = useState('');

  /* invites list */
  const [invites,       setInvites]       = useState<Invite[]>([]);
  const [invLoading,    setInvLoading]    = useState(true);
  const [page,          setPage]          = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  /* revoke confirm */
  const [revokeId,    setRevokeId]    = useState<string | null>(null);
  const [revoking,    setRevoking]    = useState(false);
  const [revokeError, setRevokeError] = useState('');

  /* access requests */
  const [requests,   setRequests]   = useState<AccessRequest[]>([]);
  const [reqLoading, setReqLoading] = useState(false);
  const [reqError,   setReqError]   = useState('');
  const [actioning,  setActioning]  = useState<Set<string>>(new Set());
  const [sourceRequestId, setSourceRequestId] = useState<string | null>(null);

  /* ── Data fetchers ── */

  const fetchInvites = async (p: number) => {
    setInvLoading(true);
    try {
      const res   = await api.get('/api/v1/admin/invites', { params: { page: p, size: PAGE_SIZE } });
      const paged = unwrap<Paged<Invite>>(res.data);
      setInvites(paged.content ?? []);
      setTotalPages(paged.totalPages ?? 0);
      setTotalElements(paged.totalElements ?? 0);
    } catch { /* empty state shown */ } finally { setInvLoading(false); }
  };

  const fetchRequests = async () => {
    setReqLoading(true);
    setReqError('');
    try {
      const res   = await api.get('/api/v1/admin/access-requests', { params: { status: 'pending' } });
      const paged = unwrap<Paged<AccessRequest>>(res.data);
      setRequests(paged.content ?? []);
    } catch { setReqError('Failed to load access requests.'); }
    finally { setReqLoading(false); }
  };

  useEffect(() => { fetchInvites(0); }, []);

  useEffect(() => {
    if (tab === 'requests' && !reqLoading && requests.length === 0 && !reqError) {
      fetchRequests();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  /* ── Handlers ── */

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    setGenError(''); setInviteUrl(''); setGenerating(true);
    try {
      const res    = await api.post('/api/v1/admin/invites/generate', {
        societyName, contactName, contactEmail: email, plan: planField, expiryHours: expiry,
        sourceAccessRequestId: sourceRequestId,
      });
      const result = unwrap<{ inviteUrl: string }>(res.data);
      setInviteUrl(result.inviteUrl ?? '');
      fetchInvites(page);

      // Only now mark the originating access request handled — if this call
      // never succeeds (refresh, lost connection, validation error), the
      // request stays visible in the Access Requests tab so nothing is lost.
      if (sourceRequestId) {
        const doneId = sourceRequestId;
        setSourceRequestId(null);
        api.patch(`/api/v1/admin/access-requests/${doneId}/convert`)
          .then(() => setRequests(r => r.filter(x => x.id !== doneId)))
          .catch(() => { /* request stays in the pending list; harmless to retry */ });
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setGenError(msg || 'Failed to generate invite. Please try again.');
    } finally { setGenerating(false); }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSend = async () => {
    setSending(true);
    setSendSuccess(false);
    setSendError('');
    try {
      await api.post('/api/v1/admin/invites/send', { contactEmail: email, inviteUrl, societyName });
      setSendSuccess(true);
      setTimeout(() => setSendSuccess(false), 3000);
    } catch {
      setSendError('Failed to send email. Copy and share the link manually.');
      setTimeout(() => setSendError(''), 4000);
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeId) return;
    const snapshot = [...invites];
    setRevoking(true); setRevokeError('');
    setInvites(iv => iv.filter(x => x.id !== revokeId));
    try {
      await api.delete(`/api/v1/admin/invites/${revokeId}`);
      setRevokeId(null);
      setTotalElements(n => n - 1);
      // Revoking can reopen the access request this invite came from.
      fetchRequests();
    } catch {
      setInvites(snapshot);
      setRevokeError('Failed to revoke. Please try again.');
    } finally { setRevoking(false); }
  };

  const handleConvert = (req: AccessRequest) => {
    // Prefill the Generate Invite form and remember the source request — it's
    // only actually marked handled once an invite is successfully generated
    // (see handleGenerate), so it can't get lost if this tab switch is the
    // last thing that happens (refresh, closed tab, etc.).
    setSourceRequestId(req.id);
    setSocietyName(req.societyName);
    setContactName(req.contactName);
    setEmail(req.email);
    setTab('invites');
  };

  const handleDismiss = async (id: string) => {
    const snapshot = [...requests];
    setRequests(r => r.filter(x => x.id !== id));
    try { await api.patch(`/api/v1/admin/access-requests/${id}/dismiss`); }
    catch { setRequests(snapshot); }
  };

  const goToPage = (p: number) => { setPage(p); fetchInvites(p); };

  /* ── Column definitions ── */

  const inviteColumns = [
    {
      key: 'society', header: 'Society',
      cellStyle: { fontWeight: 500 },
      render: (inv: Invite) => inv.societyName,
    },
    {
      key: 'email', header: 'Email',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (inv: Invite) => inv.invitedEmail ?? inv.contactEmail ?? inv.email ?? '—',
    },
    {
      key: 'plan', header: 'Plan',
      render: (inv: Invite) => <Badge label={inv.plan} {...PLAN_STYLE[inv.plan]} />,
    },
    {
      key: 'created', header: 'Created',
      cellStyle: { color: 'var(--nx-text-secondary)', whiteSpace: 'nowrap' as const },
      render: (inv: Invite) => fmt(inv.createdAt),
    },
    {
      key: 'expires', header: 'Expires',
      cellStyle: { color: 'var(--nx-text-secondary)', whiteSpace: 'nowrap' as const },
      render: (inv: Invite) => fmt(inv.expiresAt),
    },
    {
      key: 'status', header: 'Status',
      render: (inv: Invite) => (
        <Badge {...STATUS_STYLE[inv.status]} label={STATUS_STYLE[inv.status].label} />
      ),
    },
    {
      key: 'actions', header: '',
      cellStyle: { textAlign: 'right' as const },
      render: (inv: Invite) => inv.status === 'PENDING' ? (
        <button
          onClick={() => { setRevokeError(''); setRevokeId(inv.id); }}
          style={{
            padding: '5px 12px', borderRadius: 6,
            border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)',
            color: '#DC2626', fontSize: 12, fontWeight: 500, cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}
        >
          Revoke
        </button>
      ) : null,
    },
  ];

  const requestColumns = [
    {
      key: 'name', header: 'Name',
      cellStyle: { fontWeight: 500 },
      render: (req: AccessRequest) => req.contactName,
    },
    {
      key: 'society', header: 'Society',
      render: (req: AccessRequest) => req.societyName,
    },
    {
      key: 'city', header: 'City',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (req: AccessRequest) => req.city ?? '—',
    },
    {
      key: 'phone', header: 'Phone',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (req: AccessRequest) => req.phone ?? '—',
    },
    {
      key: 'email', header: 'Email',
      cellStyle: { color: 'var(--nx-text-secondary)' },
      render: (req: AccessRequest) => req.email,
    },
    {
      key: 'submitted', header: 'Submitted',
      cellStyle: { color: 'var(--nx-text-secondary)', whiteSpace: 'nowrap' as const },
      render: (req: AccessRequest) => fmt(req.createdAt),
    },
    {
      key: 'actions', header: 'Actions',
      render: (req: AccessRequest) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => handleConvert(req)}
            disabled={actioning.has(req.id)}
            className="nx-btn nx-btn-primary nx-btn-sm"
            style={{ opacity: actioning.has(req.id) ? 0.6 : 1, cursor: actioning.has(req.id) ? 'not-allowed' : 'pointer' }}
          >
            Convert to Invite
          </button>
          <button
            onClick={() => handleDismiss(req.id)}
            disabled={actioning.has(req.id)}
            style={{
              padding: '6px 14px', borderRadius: 6, border: '1px solid var(--nx-border)',
              background: '#fff', color: 'var(--nx-text-secondary)', fontSize: 12,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font-body)',
            }}
          >
            Dismiss
          </button>
        </div>
      ),
    },
  ];

  /* ── Render ── */

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '10px 22px', background: 'none', border: 'none',
    borderBottom: `2px solid ${active ? '#1A6CF6' : 'transparent'}`,
    color: active ? '#1A6CF6' : 'var(--nx-text-secondary)',
    fontWeight: active ? 600 : 400, fontSize: 14, cursor: 'pointer', marginBottom: -1,
    fontFamily: 'var(--font-body)', transition: 'color 150ms',
  });

  return (
    <div style={{ background: '#f8fafc', minHeight: '100%', padding: 32, fontFamily: 'var(--font-body)' }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
          Invite Management
        </h1>
        <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>
          Generate invite links for society admins and manage inbound access requests.
        </p>
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--nx-border)', marginBottom: 24 }}>
        <button style={tabBtnStyle(tab === 'invites')}  onClick={() => setTab('invites')}>Invites</button>
        <button style={tabBtnStyle(tab === 'requests')} onClick={() => setTab('requests')}>
          Access Requests
          {requests.length > 0 && (
            <span style={{ marginLeft: 8, padding: '2px 7px', borderRadius: 99, background: 'rgba(245,158,11,0.15)', color: '#D97706', fontSize: 11, fontWeight: 700 }}>
              {requests.length}
            </span>
          )}
        </button>
      </div>

      {/* ════════════ INVITES TAB ════════════ */}
      {tab === 'invites' && (
        <>
          {/* Generate form card */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', padding: '22px 24px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 18 }}>
              Generate Invite Link
            </h2>
            <form onSubmit={handleGenerate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Society Name
                  </label>
                  <input required className="nx-input" value={societyName} onChange={e => setSocietyName(e.target.value)} placeholder="Green Valley Residency" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Contact Name
                  </label>
                  <input required className="nx-input" value={contactName} onChange={e => setContactName(e.target.value)} placeholder="Suresh Iyer" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Contact Email
                  </label>
                  <input required type="email" className="nx-input" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@society.org" />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Plan
                  </label>
                  <select className="nx-input" value={planField} onChange={e => setPlanField(e.target.value as Plan)}>
                    {PLAN_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)', marginBottom: 6, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Expires in (hours)
                  </label>
                  <input type="number" min={1} max={720} className="nx-input" value={expiry} onChange={e => setExpiry(Number(e.target.value))} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 14, marginTop: 4 }}>
                  <button
                    type="submit"
                    disabled={generating}
                    className="nx-btn nx-btn-primary nx-btn-sm"
                    style={{ opacity: generating ? 0.7 : 1, cursor: generating ? 'not-allowed' : 'pointer' }}
                  >
                    {generating ? 'Generating…' : 'Generate Invite'}
                  </button>
                  {genError && <span style={{ fontSize: 12, color: 'var(--nx-red)' }}>{genError}</span>}
                </div>
              </div>
            </form>

            {/* Result box */}
            {inviteUrl && (
              <div style={{ marginTop: 20, padding: '14px 16px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#059669', marginBottom: 10 }}>
                  ✓ Invite link ready
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    readOnly
                    value={inviteUrl}
                    className="nx-input"
                    style={{ flex: 1, fontSize: 12, background: '#f8fafc', cursor: 'text' }}
                    onClick={e => (e.target as HTMLInputElement).select()}
                  />
                  <button onClick={handleCopy} className="nx-btn nx-btn-outline nx-btn-sm" title="Copy link" style={{ flexShrink: 0 }}>
                    {copied ? <Check size={13} style={{ color: '#059669' }} /> : <Copy size={13} />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending}
                    className="nx-btn nx-btn-outline nx-btn-sm"
                    style={{ flexShrink: 0, opacity: sending ? 0.7 : 1, cursor: sending ? 'not-allowed' : 'pointer', color: sendSuccess ? '#059669' : undefined }}
                  >
                    <Mail size={13} />
                    {sending ? 'Sending…' : sendSuccess ? 'Sent!' : 'Send'}
                  </button>
                  {sendError && (
                    <span style={{ fontSize: 11, color: 'var(--nx-red)', whiteSpace: 'nowrap' }}>{sendError}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Invites table card */}
          <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                  All Invites
                </span>
                {totalElements > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{totalElements} total</span>
                )}
              </div>
              <button onClick={() => fetchInvites(page)} className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 6 }}>
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
            <DataGrid
              columns={inviteColumns}
              rows={invites}
              rowKey={inv => inv.id}
              loading={invLoading}
              emptyText="No invites yet — generate one above."
              rowStyle={inv => ({ opacity: inv.status === 'EXPIRED' || inv.status === 'REVOKED' ? 0.5 : 1 })}
              page={page}
              totalPages={totalPages}
              onPageChange={goToPage}
            />
          </div>
        </>
      )}

      {/* ════════════ ACCESS REQUESTS TAB ════════════ */}
      {tab === 'requests' && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid var(--nx-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--nx-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ fontFamily: 'Syne, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)' }}>
                Pending Requests
              </span>
              {requests.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{requests.length} pending</span>
              )}
            </div>
            <button onClick={fetchRequests} className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 6 }}>
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
          {reqError ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--nx-red)', fontSize: 13 }}>{reqError}</div>
          ) : (
            <DataGrid
              columns={requestColumns}
              rows={requests}
              rowKey={req => req.id}
              loading={reqLoading}
              emptyText="No pending access requests."
            />
          )}
        </div>
      )}

      {/* ════════════ REVOKE CONFIRM DIALOG ════════════ */}
      {revokeId && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={e => { if (e.target === e.currentTarget) { setRevokeId(null); setRevokeError(''); } }}
        >
          <div style={{ background: '#fff', borderRadius: 16, padding: '28px 28px', maxWidth: 420, width: '90%', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 20 }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: 'rgba(245,158,11,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AlertTriangle size={20} style={{ color: '#D97706' }} />
              </div>
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--nx-text-primary)', marginBottom: 5, fontFamily: 'Syne, sans-serif' }}>
                  Revoke invite?
                </p>
                <p style={{ fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>
                  This link will be immediately invalidated. The recipient will need a new invite to register.
                </p>
              </div>
            </div>
            {revokeError && (
              <p style={{ fontSize: 12, color: 'var(--nx-red)', marginBottom: 14 }}>{revokeError}</p>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setRevokeId(null); setRevokeError(''); }}
                style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid var(--nx-border)', background: '#fff', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font-body)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={revoking}
                style={{
                  padding: '9px 20px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: '#fff', cursor: revoking ? 'not-allowed' : 'pointer',
                  fontSize: 13, fontWeight: 600, opacity: revoking ? 0.7 : 1,
                  fontFamily: 'var(--font-body)', display: 'flex', alignItems: 'center', gap: 8,
                }}
              >
                {revoking && (
                  <span style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'nx-spin 0.7s linear infinite', display: 'inline-block' }} />
                )}
                {revoking ? 'Revoking…' : 'Yes, Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteManagementPage;
